import { useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowRight, Sparkles, Search, ShieldCheck, CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useIsMobile } from "@/hooks/useIsMobile"

const SAMPLE_CHIPS = [
  "100x 550W Solar Panels",
  "1,000m Mulberry Silk 250gsm",
  "500x CNC Aluminium Brackets",
]

export function FinalCTA() {
  const isMobile = useIsMobile(640)
  const [quickQuery, setQuickQuery] = useState("")
  const navigate = useNavigate()

  const handleQuickSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const query = quickQuery.trim()
    if (query) {
      navigate({
        to: "/explore/suppliers",
        search: { searchTerm: query }
      })
    } else {
      navigate({ to: "/signup" })
    }
  }

  const selectChip = (chip: string) => {
    setQuickQuery(chip)
  }

  return (
    <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#8a0016] via-[#be001e] to-[#e6202f] grid grid-cols-1 lg:grid-cols-12 items-center shadow-2xl shadow-primary/20"
      >
        {/* Background Decorative Circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-black/20 rounded-full blur-3xl pointer-events-none" />

        {/* Left column — Copy, Quick Search, CTAs (7 cols) */}
        <div className="p-8 md:p-12 lg:p-16 flex flex-col gap-6 text-white relative z-10 lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs font-bold text-white w-fit">
            <Sparkles className="size-3.5" />
            <span>Instant RFQ Matching</span>
          </div>

          <h2 className="font-sans text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight select-none">
            Ready to transform your B2B supply chain?
          </h2>

          <p className="text-sm md:text-base text-white/90 leading-relaxed max-w-lg font-normal">
            Join enterprise procurement teams and audited Indian manufacturers using ENGA to automate RFQs, eliminate middlemen, and protect capital with milestone escrow.
          </p>

          {/* Quick Try-Box Form */}
          <form 
            onSubmit={handleQuickSearch}
            className="flex flex-col sm:flex-row gap-2 max-w-xl w-full pt-1"
          >
            <div className="relative flex-grow flex items-center">
              <Search className="size-4 text-white/60 absolute left-4 pointer-events-none z-10" />
              <input
                type="text"
                value={quickQuery}
                onChange={(e) => setQuickQuery(e.target.value)}
                placeholder={isMobile ? "Search custom product..." : "What custom product are you looking to source?"}
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/15 border border-white/30 text-white placeholder:text-white/60 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm transition-all"
              />
            </div>
            <Button
              type="submit"
              className="bg-white hover:bg-white/90 text-primary px-6 py-3 h-auto rounded-xl text-xs sm:text-sm font-bold shadow-lg shadow-black/10 active:scale-95 transition-all cursor-pointer shrink-0"
            >
              <span>Instant Match</span>
            </Button>
          </form>

          {/* 1-Click Sample Chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-bold text-white/75 mr-1">Try:</span>
            {SAMPLE_CHIPS.map((chip, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => selectChip(chip)}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 border border-white/15 text-white transition-all cursor-pointer"
              >
                {chip}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 text-xs font-semibold text-white/80 pt-1 border-t border-white/15">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-white" />
              <span>100% Escrow Protected</span>
            </div>
            <span>•</span>
            <span>Zero obligation line-item bids</span>
          </div>
        </div>

        {/* Right column — Visual port image with overlay (5 cols) */}
        <div className="h-full min-h-[300px] lg:min-h-[460px] self-stretch relative lg:col-span-5">
          <img
            className="absolute inset-0 w-full h-full object-cover"
            alt="Bustling global shipping port with cargo ships and containers during blue hour, representing professional trade efficiency"
            src="/shipping_port.png"
          />
          <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#8a0016]/65 via-[#be001e]/20 to-transparent pointer-events-none" />
        </div>

      </motion.div>
    </section>
  )
}


